import { cloneDeep, merge, isEqual } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { AdError, config } from '../../../constants/ads';
import { AdsContext } from '../../../contexts';
import styles from './Ad.module.scss';
import renderTemplate from '@buzzfeed/adlib/dist/module/services/template-utils';
import AdExTemplate from '@buzzfeed/adlib/dist/module/components/ex/index.svelte';

export default class AdWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            adConfig: null,
        };
    }

    static getDerivedStateFromProps(props) {
        const adConfig = cloneDeep(config[props.type]); // avoid mutating the shared config
        adConfig.advertiserContext = false;
        adConfig.targeting = merge(adConfig.targeting, props.targeting);
        return { adConfig };
    }

    render() {
        return (
            <AdsContext.Consumer>
                {(adsContext) => (
                    <Ad
                        adConfig={this.state.adConfig}
                        context={adsContext}
                        className={this.props.className}
                    />
                )}
            </AdsContext.Consumer>
        );
    }
}

AdWrapper.defaultProps = {
    className: '',
};

AdWrapper.propTypes = {
    type: PropTypes.string.isRequired,
    targeting: PropTypes.object,
    className: PropTypes.string,
};

class Ad extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            // loading | active | collapsed | inactive
            loadState: 'inactive',
        };

        this.container = React.createRef();
        this.adWireframe = null;
        this.nativeAd = null;
        this.isInitializingAd = false;
    }

    setLoadState(loadState) {
        if (this.state.loadState !== loadState) {
            this.setState({ loadState });
        }
    }

    renderAdWireframe() {
        if (this.adWireframe) {
            this.adWireframe.$destroy();
        }
        const adConfig = this.props.adConfig;
        this.adWireframe = renderTemplate({
            template: AdExTemplate,
            target: this.container.current,
            props: {
                slot: adConfig,
            },
        });
    }

    async createAd() {
        this.isInitializingAd = true;

        if (!this.adWireframe) {
            this.renderAdWireframe();
        }

        const [adlibCore, adlibComponents] =
          await import(/* webpackChunkName: "bfadlib" */ '../../../services/adlib')
              .then((module) => [module.core, module.components]);
        const nativeAd = new adlibComponents.AdExBio({
            ...this.props.context,
            element: this.container.current,
            config: this.props.adConfig,
            // @todo ads - Use the adlib plugin for wireframes and remove `slotRenderEnded` handlers
            defaultWireframes: false,
        });
        nativeAd.init()
            .catch((reason) => {
                if (reason instanceof adlibCore.Disabled) {
                    return reason;
                } else {
                    return Promise.reject(reason);
                }
            });
        this.props.context.eventBus.on(
            `gpt:slotRenderEnded:${this.props.adConfig.adPos}-${nativeAd.instanceId}`,
            this.handleSlotRenderEnded
        );

        this.nativeAd = nativeAd;
        this.isInitializingAd = false;
    }

    destroyAd(props) {
        if (this.nativeAd) {
            this.nativeAd.destroy();
            props.context.eventBus.off(
                `gpt:slotRenderEnded:${props.adConfig.adPos}-${this.nativeAd.instanceId}`,
                this.handleAdRender
            );
            this.nativeAd = null;
        }
        if (this.adWireframe) {
            this.adWireframe.$destroy();
            this.adWireframe = null;
        }

        this.isInitializingAd = false;
    }

  handleSlotRenderEnded = (gptEv) => {
      this.setLoadState(gptEv.isEmpty ? 'collapsed' : 'active');
  }

  componentDidMount() {
      this.renderAdWireframe();
      if (this.props.context !== null) {
          this.createAd();
      }
  }

  componentDidUpdate(prevProps) {
      const props = this.props;
      // wait for `AdsContext` to load
      if (props.context === null) { return; }
      // do not create an ad if it's already being created (since `createAd` is async)
      if (this.isInitializingAd) { return; }
      // do not re-request an ad if the config and page layout are the same
      if (
          this.nativeAd
      && isEqual(prevProps.adConfig, props.adConfig)
      ) { return; }
      if (props.context.error instanceof AdError) {
          if (this.state.loadState !== 'inactive') {
              this.setLoadState('collapsed');
          }
          return;
      }

      // eslint-disable-next-line max-len
      // @todo ads - Delegate the refresh functionality to adlib instead of destroying and re-creating an ad
      if (this.nativeAd) {
          this.destroyAd(prevProps);
      }

      this.createAd(); // no need to await
      this.setLoadState('loading');
  }

  componentWillUnmount() {
      this.destroyAd(this.props);
  }

  render() {
      const adConfig = this.props.adConfig;

      let stateStyle = '';
      switch (this.state.loadState) {
      case 'active':
          stateStyle = styles.AdActive;
          break;
      case 'inactive':
          stateStyle = styles.AdInactive;
          break;
      case 'collapsed':
          stateStyle = styles.AdCollapsed;
          break;
      case 'loading':
          stateStyle = styles.AdLoading;
      }

      return (
          <div
              ref={this.container}
              // eslint-disable-next-line max-len
              className={`${styles.Ad} ${stateStyle} Ad Ad--${adConfig.adPos} Ad--${this.state.loadState} ${this.props.className}`}
          />
      );
  }
}

Ad.propTypes = {
    adConfig: PropTypes.object.isRequired,
    context: PropTypes.object,
    className: PropTypes.string,
};
